import {
  gsm7ExtendedOnlyRegex,
  gsm7ExtendedRegex,
  gsm7Regex,
  smsSegmentCapacity,
} from './gsmCharacters';
import {
  MeasureSmsOptions,
  SmsMeasurement,
  SmsEncoding,
} from './measureSms.types';

/** Returns the encoding the given text would be sent with. */
const detectEncoding = (text: string): SmsEncoding => {
  if (text.match(gsm7Regex)) {
    return 'GSM-7';
  }
  if (text.match(gsm7ExtendedRegex)) {
    return 'GSM-7-EXTENDED';
  }
  return 'UTF-16';
};

/**
 * Returns the number of units the given text occupies on the wire. Extension
 * characters take two septets, everything else takes one unit.
 */
const countEncodedUnits = (text: string, encoding: SmsEncoding): number => {
  if (encoding !== 'GSM-7-EXTENDED') {
    return text.length;
  }

  return [...text].reduce(
    (length, character) =>
      length + (character.match(gsm7ExtendedOnlyRegex) ? 2 : 1),
    0,
  );
};

/**
 * Returns a fresh global copy of the given pattern. Fresh, because a `/g` regex
 * carries its `lastIndex` between uses and the caller may well be sharing this
 * one with a validator. Global, so that every placeholder is removed rather
 * than only the first.
 */
const toGlobalPattern = (pattern: RegExp): RegExp =>
  new RegExp(
    pattern.source,
    pattern.global ? pattern.flags : `${pattern.flags}g`,
  );

/** Removes the template placeholders and reports how many there were. */
const stripPlaceholders = (
  text: string,
  pattern?: RegExp,
): { text: string; placeholderCount: number } => {
  if (!pattern) {
    return { text, placeholderCount: 0 };
  }

  const globalPattern = toGlobalPattern(pattern);

  return {
    text: text.replace(globalPattern, ''),
    placeholderCount: text.match(globalPattern)?.length ?? 0,
  };
};

/**
 * Splits the distinct characters of the text into the ones GSM-7 cannot carry
 * at all and the ones it carries at two septets each.
 */
const classifyCharacters = (
  text: string,
): Pick<SmsMeasurement, 'nonGsmCharacters' | 'extendedCharacters'> => {
  const nonGsmCharacters: string[] = [];
  const extendedCharacters: string[] = [];

  for (const character of new Set([...text])) {
    if (character.match(gsm7Regex)) {
      continue;
    }

    if (character.match(gsm7ExtendedOnlyRegex)) {
      extendedCharacters.push(character);
    } else {
      nonGsmCharacters.push(character);
    }
  }

  return { nonGsmCharacters, extendedCharacters };
};

/**
 * Describes what an sms costs to send: the plain character count the limit is
 * compared against, the encoding, and the number of segments.
 */
export const measureSms = (
  text: string,
  { limit, reservedLength = 0, placeholderPattern }: MeasureSmsOptions,
): SmsMeasurement => {
  const { text: measured, placeholderCount } = stripPlaceholders(
    text,
    placeholderPattern,
  );

  const encoding = detectEncoding(measured);
  const encodedUnits = countEncodedUnits(measured, encoding) + reservedLength;
  const { single, multiple } = smsSegmentCapacity[encoding];

  return {
    encoding,
    characterCount: measured.length + reservedLength,
    limit,
    segments: Math.ceil(
      encodedUnits / (encodedUnits <= single ? single : multiple),
    ),
    ...classifyCharacters(measured),
    placeholderCount,
    countIsMinimum: placeholderCount > 0,
  };
};
